/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef } from 'react'
import { UIMessage } from 'ai'
import { InvocationTools } from '../utils/invocation-tools';
import { getMessageText, getToolCalls, getMessageTimestamp } from '../utils/message-utils';

function createFileSearchGroups(messages: UIMessage[], isLoading = false) {
  const filteredMessages = isLoading 
    ? messages.filter((_, index) => index !== messages.length - 1)
    : messages;
    
  return filteredMessages
    .map((message) => {
      const toolOutputs = getToolCalls(message)
        .filter((call) => call.toolName !== InvocationTools.WEB_SEARCH_TOOL && call.toolName !== 'externalSearchTool')
        .flatMap((call) => normalizeFileResults(call.output));

      return {
        id: message.id,
        messageContent: getMessageText(message),
        results: toolOutputs,
        timestamp: getMessageTimestamp(message)
      };
    })
    .filter((group) => group.results && group.results.length > 0)
}

function createWebSearchGroups(messages: UIMessage[], isLoading = false) {
  const filteredMessages = isLoading 
    ? messages.filter((_, index) => index !== messages.length - 1)
    : messages;

  return filteredMessages
    .map((message) => {
      const webResults = getToolCalls(message)
        .filter((call) => call.toolName === InvocationTools.WEB_SEARCH_TOOL || call.toolName === 'externalSearchTool')
        .flatMap((call) => normalizeWebResults(call.output));

      return {
        id: message.id,
        messageContent: getMessageText(message),
        results: webResults,
        timestamp: getMessageTimestamp(message)
      };
    })
    .filter((group) => group.results && group.results.length > 0)
}

export function SearchGroupsProvider({ 
  messages, 
  isLoading = false 
}: { 
  messages: UIMessage[], 
  isLoading?: boolean 
}) {
  const seenGroupsRef = useRef(new Set<string>());
  
  const toolInvocationsFingerprint = useMemo(() => {
    const messagesToProcess = isLoading 
      ? messages.filter((_, index) => index !== messages.length - 1)
      : messages;
    
    return messagesToProcess
      .flatMap(message => getToolCalls(message))
      .map(call => `${call.toolName}:${JSON.stringify(call.output ?? call.input ?? '')}`)
      .join('|');
  }, [messages, isLoading])
  
  const searchGroups = useMemo(() => {
    const fileSearchGroups = createFileSearchGroups(messages, isLoading);
    const webSearchGroups = createWebSearchGroups(messages, isLoading);
    
    const fileSearchResults = fileSearchGroups.map(group => {
      const isNew = !seenGroupsRef.current.has(group.id);
      seenGroupsRef.current.add(group.id);
      return {
        ...group,
        isNew
      };
    }).reverse();

    const webSearchResults = webSearchGroups.map(group => {
      const isNew = !seenGroupsRef.current.has(group.id);
      seenGroupsRef.current.add(group.id);
      return {
        ...group,
        isNew
      };
    }).reverse();

    return {
      fileSearchResults,
      webSearchResults,
    }
  }, [toolInvocationsFingerprint, isLoading])

  return { searchGroups }
}

export function useSearchGroups(messages: UIMessage[], isLoading?: boolean) {
  return SearchGroupsProvider({ messages, isLoading }).searchGroups
}

function normalizeFileResults(output: unknown): SearchResult[] {
  if (!output) return [];
  if (Array.isArray(output)) {
    return output as SearchResult[];
  }
  if (Array.isArray((output as any)?.files)) {
    return (output as any).files as SearchResult[];
  }
  if (Array.isArray((output as any)?.results)) {
    return (output as any).results as SearchResult[];
  }
  return [];
}

function normalizeWebResults(output: unknown): WebSearchToolInvocationSearchResult[] {
  if (!output) return [];
  const inner = (output as WebToolInvocation)?.result ?? output;
  if (Array.isArray((inner as any)?.result)) {
    return (inner as WebToolInvocation).result?.result ?? [];
  }
  if (Array.isArray(inner)) {
    return inner as WebSearchToolInvocationSearchResult[];
  }
  return [];
}
